// A tool call against a customer-connected MCP server. Everything the server
// returns is untrusted: text goes through the markdown renderer, which escapes
// HTML; a resource URI is only safe in an href; image bytes reach the DOM as a
// data URL, never as markup.

import { isString } from "../utils/schemaUtils";

export interface MCPResultServer {
  slug: string;
  name: string;
  // Empty when the server has no icon; only an https URL is ever loaded.
  iconUrl: string;
}

export type MCPResultContent =
  // `truncated` marks text cut at the converter's size cap.
  | { kind: "text"; text: string; truncated: boolean }
  | { kind: "image"; mimeType: string; data: string }
  // The converter dropped the bytes of an image over its size cap.
  | { kind: "image"; placeholder: true }
  // `mimeType` and `text` are empty when the server sent none; `uri` may be
  // empty too. `truncated` marks text cut at the converter's size cap.
  | { kind: "resource"; uri: string; mimeType: string; text: string; truncated: boolean }
  | { kind: "json"; json: unknown };

export interface MCPResultPayload {
  type: "mcp_result";
  server: MCPResultServer;
  tool: string;
  args: Record<string, unknown>;
  // The server reported the call failed; `content` then holds its error text.
  isError: boolean;
  content: MCPResultContent[];
}

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

const parseContent = (v: unknown): MCPResultContent | null => {
  if (!isPlainObject(v)) return null;
  switch (v.kind) {
    case "text":
      return isString(v.text) ? { kind: "text", text: v.text, truncated: v.truncated === true } : null;
    case "image":
      if (v.placeholder === true) return { kind: "image", placeholder: true };
      return isString(v.mimeType) && isString(v.data) ? { kind: "image", mimeType: v.mimeType, data: v.data } : null;
    case "resource":
      if (!isString(v.uri)) return null;
      if (v.mimeType != null && !isString(v.mimeType)) return null;
      if (v.text != null && !isString(v.text)) return null;
      return {
        kind: "resource",
        uri: v.uri,
        mimeType: v.mimeType ?? "",
        text: v.text ?? "",
        truncated: v.truncated === true,
      };
    case "json":
      return "json" in v ? { kind: "json", json: v.json } : null;
    default:
      return null;
  }
};

export const parseMCPResultPayload = (v: unknown): MCPResultPayload | null => {
  if (!isPlainObject(v)) return null;
  if (v.type !== "mcp_result") return null;
  if (!isPlainObject(v.server)) return null;
  // iconUrl is omitted, not empty, when the server has no icon.
  if (!isString(v.server.slug) || !isString(v.server.name)) return null;
  if (v.server.iconUrl != null && !isString(v.server.iconUrl)) return null;
  if (!isString(v.tool)) return null;
  // A Go nil map or slice marshals as null, so an argument-less call or an
  // empty result arrives that way rather than as {} or [].
  if (v.args != null && !isPlainObject(v.args)) return null;
  if (v.content != null && !Array.isArray(v.content)) return null;
  const content: MCPResultContent[] = [];
  for (const item of v.content ?? []) {
    const parsed = parseContent(item);
    if (!parsed) return null;
    content.push(parsed);
  }
  return {
    type: "mcp_result",
    server: { slug: v.server.slug, name: v.server.name, iconUrl: v.server.iconUrl ?? "" },
    tool: v.tool,
    args: v.args ?? {},
    isError: v.isError === true,
    content,
  };
};
